import { PRODUCT_KIND_LABELS, PRODUCT_STATE_LABELS } from '@repo/types';
import { PRODUCT_KIND, PRODUCT_STATE } from '@theailab-dev/parchment';
import type { ProductKind, ProductState } from '@theailab-dev/parchment';
import React, { useState, useEffect } from 'react';
import { HiSearch, HiRefresh, HiPlus } from 'react-icons/hi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TablePageLayout } from '../../components/Layout/TablePageLayout';
import {
  Button,
  Input,
  Card,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useProducts } from '../../hooks/useProducts';

/**
 * Products 컴포넌트 - 상품 목록 페이지
 */
const Products: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL 쿼리 파라미터 읽기
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const currentLimit = parseInt(searchParams.get('limit') || '20', 10);
  const searchKeyword = searchParams.get('search') || '';
  const kindFilter = searchParams.get('kind') || '';
  const stateFilter = searchParams.get('state') || '';

  // 검색 입력 필드 local state
  const [searchQuery, setSearchQuery] = useState(searchKeyword);

  const queryParams = {
    page: currentPage,
    limit: currentLimit,
    sortBy: 'createdAt' as const,
    sortOrder: 'DESC' as const,
    filterKey: searchKeyword ? ('search' as const) : undefined,
    filterValue: searchKeyword || undefined,
    // kind: kindFilter ? (kindFilter as typeof PRODUCT_KIND.SINGLE | typeof PRODUCT_KIND.ROADMAP) : undefined,
    // state: stateFilter ? (stateFilter as typeof PRODUCT_STATE.NORMAL | typeof PRODUCT_STATE.HIDDEN) : undefined,
    kind: Object.values(PRODUCT_KIND).includes(kindFilter as ProductKind) ? (kindFilter as ProductKind) : undefined,
    state: Object.values(PRODUCT_STATE).includes(stateFilter as ProductState)
      ? (stateFilter as ProductState)
      : undefined,
  };

  const { data, isLoading, isError, error, refetch } = useProducts(queryParams);

  useEffect(() => {
    setSearchQuery(searchKeyword);
  }, [searchKeyword]);

  // URL에 page 파라미터가 없으면 기본값 "1" 설정 (초기 마운트 시에만)
  useEffect(() => {
    if (!searchParams.has('page')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', '1');
      setSearchParams(newParams, { replace: true });
    }
  }, []);

  const products = data?.data || [];
  const meta = data?.meta;
  const page = meta?.page || 1;
  const limit = meta?.limit || 20;
  const total = meta?.total || 0;
  const totalPages = meta?.totalPages || 1;

  const handleSearch = () => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    if (searchQuery.trim()) {
      params.set('search', searchQuery.trim());
    }
    if (kindFilter) {
      params.set('kind', kindFilter);
    }
    if (stateFilter) {
      params.set('state', stateFilter);
    }
    setSearchParams(params);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleKindFilterChange = (kind: string) => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());

    if (kind) {
      params.set('kind', kind);
    }

    if (stateFilter) {
      params.set('state', stateFilter);
    }

    if (searchKeyword) {
      params.set('search', searchKeyword);
    }

    setSearchParams(params);
  };

  const handleStateFilterChange = (state: string) => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());

    if (state) {
      params.set('state', state);
    }

    if (kindFilter) {
      params.set('kind', kindFilter);
    }

    if (searchKeyword) {
      params.set('search', searchKeyword);
    }

    setSearchParams(params);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('limit', currentLimit.toString());
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', page.toString());
    setSearchParams(params);
  };

  const handleLimitChange = (newLimit: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', newLimit.toString());
    params.set('page', '1');
    setSearchParams(params);
  };

  const handleProductClick = (productId: number) => {
    // 현재 URL의 쿼리 파라미터를 포함하여 상세 페이지로 이동
    const currentPath = `/dashboard/products?${searchParams.toString()}`;
    navigate(`/dashboard/products/${productId}`, {
      state: { from: currentPath },
    });
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';

      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    } catch {
      return '-';
    }
  };

  const getKindBadgeVariant = (kind: ProductKind) => {
    switch (kind) {
      case PRODUCT_KIND.SINGLE:
        return 'success';
      case PRODUCT_KIND.ROADMAP:
        return 'warning';
      default:
        return 'neutral';
    }
  };

  const getStateBadgeVariant = (state: ProductState) => {
    switch (state) {
      case PRODUCT_STATE.NORMAL:
        return 'success';
      case PRODUCT_STATE.HIDDEN:
        return 'warning';
      default:
        return 'neutral';
    }
  };

  if (isError) {
    return (
      <div className="flex h-full w-full items-center justify-center p-8">
        <Card className="w-full max-w-md p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '상품 목록을 불러오는데 실패했습니다.'}
          </p>
          <Button onClick={() => refetch()} variant="outline">
            다시 시도
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <TablePageLayout
      title="상품 목록"
      description="전체 상품을 관리하고 모니터링하세요"
      action={
        <Button onClick={() => navigate('/dashboard/products/new')} leftIcon={<HiPlus />}>
          상품 추가
        </Button>
      }
      filters={
        <>
          <select
            value={kindFilter}
            onChange={(e) => handleKindFilterChange(e.target.value)}
            className="min-w-[140px] cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="">전체 유형</option>
            <option value={PRODUCT_KIND.SINGLE}>단일 상품</option>
            <option value={PRODUCT_KIND.ROADMAP}>로드맵</option>
          </select>

          <select
            value={stateFilter}
            onChange={(e) => handleStateFilterChange(e.target.value)}
            className="min-w-[140px] cursor-pointer rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 transition-colors hover:border-slate-300 focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none">
            <option value="">전체 상태</option>
            <option value={PRODUCT_STATE.NORMAL}>정상</option>
            <option value={PRODUCT_STATE.HIDDEN}>숨김</option>
          </select>

          {(searchKeyword || kindFilter || stateFilter) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearSearch}
              leftIcon={<HiRefresh />}
              className="text-slate-500">
              초기화
            </Button>
          )}
        </>
      }
      search={
        <>
          <div className="relative w-full lg:w-72">
            <Input
              placeholder="상품 제목, 설명 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              leftIcon={<HiSearch />}
              className="w-full"
            />
          </div>
          <Button onClick={handleSearch} variant="secondary">
            검색
          </Button>
        </>
      }
      isLoading={isLoading}
      currentPage={page}
      totalPages={totalPages}
      onPageChange={handlePageChange}
      totalItems={total}
      limit={limit}
      onLimitChange={handleLimitChange}>
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingSpinner />
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">ID</TableHead>
              <TableHead className="min-w-[240px]">상품 정보</TableHead>
              <TableHead>유형</TableHead>
              <TableHead>상태</TableHead>
              <TableHead>판매 기간</TableHead>
              <TableHead>생성일</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id} onClick={() => handleProductClick(product.id)}>
                <TableCell className="text-slate-500">#{product.id}</TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900 transition-colors group-hover:text-blue-600">
                    {product.title}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant={getKindBadgeVariant(product.kind)}>{PRODUCT_KIND_LABELS[product.kind]}</Badge>
                </TableCell>
                <TableCell>
                  <Badge variant={getStateBadgeVariant(product.state)}>{PRODUCT_STATE_LABELS[product.state]}</Badge>
                </TableCell>
                <TableCell>
                  {product.openAt && product.closeAt ? (
                    <div className="text-xs">
                      <div>{formatDate(product.openAt)}</div>
                      <div className="text-slate-400">~ {formatDate(product.closeAt)}</div>
                    </div>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell nowrap className="text-slate-500">
                  {formatDate(product.createdAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {!isLoading && products.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
            <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-slate-900">상품이 없습니다</h3>
          <p className="mt-1 text-slate-500">새로운 상품을 추가하거나 검색 조건을 변경해보세요.</p>
          <Button variant="outline" className="mt-4" onClick={handleClearSearch}>
            필터 초기화
          </Button>
        </div>
      )}
    </TablePageLayout>
  );
};

export default Products;
